import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

const safeJson = async (res) => {
  const raw = await res.text();
  try {
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const normalizeList = (data) => {
  const d = data?.data ?? data;
  const candidate =
    d?.notifications ??
    d?.items ??
    d?.records ??
    d?.result ??
    d?.data ??
    (Array.isArray(d) ? d : []);
  return Array.isArray(candidate) ? candidate : [];
};

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;
      if (!token) return rejectWithValue("Please login first.");
      if (!userId) return rejectWithValue("UserId not found in token.");

      const url =
        `${API_BASE}/Notification/GetNotifications?` +
        new URLSearchParams({ userId }).toString();

      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: token }, // if Bearer required: `Bearer ${token}`
      });

      const data = await safeJson(res);

      if (!res.ok) {
        return rejectWithValue(
          data?.message || data?.error || `Request failed (${res.status})`
        );
      }

      return { items: normalizeList(data) };
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch notifications");
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnreadCount",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;
      if (!token) return rejectWithValue("Please login first.");
      if (!userId) return rejectWithValue("UserId not found in token.");

      const url =
        `${API_BASE}/Notification/GetUnreadNotificationCount?` +
        new URLSearchParams({ userId }).toString();

      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: token },
      });

      const data = await safeJson(res);

      if (!res.ok) {
        return rejectWithValue(
          data?.message || data?.error || `Request failed (${res.status})`
        );
      }

      // supports {count: 3} or {unreadCount: 3} or {data: {count: 3}} etc.
      const d = data?.data ?? data;
      const count =
        Number(d?.unreadCount ?? d?.count ?? d?.totalUnread ?? 0) || 0;

      return { count };
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch unread count");
    }
  }
);

export const markNotificationsRead = createAsyncThunk(
  "notifications/markNotificationsRead",
  async ({ notificationIds }, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;
      if (!token) return rejectWithValue("Please login first.");
      if (!userId) return rejectWithValue("UserId not found in token.");

      if (!Array.isArray(notificationIds) || notificationIds.length === 0) {
        return rejectWithValue("No notifications selected.");
      }

      const url = `${API_BASE}/Notification/MarkNotificationRead`;

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          notificationIds,
        }),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        return rejectWithValue(
          data?.message || data?.error || `Request failed (${res.status})`
        );
      }

      return { notificationIds };
    } catch (err) {
      return rejectWithValue(
        err?.message || "Failed to mark notifications read"
      );
    }
  }
);

const notificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    items: [],
    unreadCount: 0,
    status: "idle", // fetch list
    unreadStatus: "idle", // fetch count
    markStatus: "idle", // patch
    error: "",
  },
  reducers: {
    clearNotificationsError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // list
      .addCase(fetchNotifications.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload?.items || [];
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch notifications";
        state.items = [];
      })

      // unread count
      .addCase(fetchUnreadCount.pending, (state) => {
        state.unreadStatus = "loading";
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadStatus = "succeeded";
        state.unreadCount = action.payload?.count ?? 0;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        state.unreadStatus = "failed";
        // don't wipe list; only show error if no list error already
        state.error =
          state.error || action.payload || "Failed to fetch unread count";
      })

      // mark read
      .addCase(markNotificationsRead.pending, (state) => {
        state.markStatus = "loading";
        state.error = "";
      })
      .addCase(markNotificationsRead.fulfilled, (state, action) => {
        state.markStatus = "succeeded";
        const ids = new Set(action.payload?.notificationIds || []);

        // Update local list to read (supports multiple possible flags)
        state.items = state.items.map((n) => {
          const id = n?.notificationId || n?.id || n?._id;
          if (!id || !ids.has(id)) return n;

          return {
            ...n,
            isRead: true,
            read: true,
            status: n?.status ?? "READ",
          };
        });

        // Decrease unread count safely
        const markedCount = action.payload?.notificationIds?.length || 0;
        state.unreadCount = Math.max(0, state.unreadCount - markedCount);
      })
      .addCase(markNotificationsRead.rejected, (state, action) => {
        state.markStatus = "failed";
        state.error = action.payload || "Failed to mark notifications read";
      });
  },
});

export const { clearNotificationsError } = notificationsSlice.actions;
export default notificationsSlice.reducer;

// selectors
export const selectNotificationsItems = (s) => s.notifications.items;
export const selectNotificationsStatus = (s) => s.notifications.status;
export const selectUnreadCount = (s) => s.notifications.unreadCount;
export const selectNotificationsError = (s) => s.notifications.error;
export const selectMarkStatus = (s) => s.notifications.markStatus;
